import React, { useEffect } from "react";
import { Route, Routes } from "react-router-dom";
// import { get } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
// import { useGetMe } from "./hooks/useAuth";
import { createUserSuccess } from "./redux/actions/authActions";
import { convertPermission } from "./utils/getPermissions";
import {
  Layout,
  WelcomePage,
  Categories,
  CreateUpdateCategory,
  Branches,
  CreateUpdateBranch,
  UserRoles,
  CreateUpdateRole,
  NotFound,
  Users,
  Products,
  Measures,
  Terminals,
  Brands,
  Cashboxes,
  Currencies,
  Countries,
  Regions,
  Districts,
  Warehouses,
  PriceTypes,
  StockOrders,
  CreateUpdateStockOrder,
  Customers,
  Payments,
  PriceSettings,
  ZIndex,
  Banners,
  Subdivisions,
  Positions,
  Employees,
  Colors,
  Stickers,
  Groups,
  Abouts,
  ProductSizes,
  Variations,
  CreateUpdateVariation,
  StockOrdersReturn,
  CreateUpdateOrdersReturn,
  Offices,
  Posts,
  Settings,
  Reviews,
  Files,
  StockReports,
  DealerBalance
} from "./containers/pageListAsync";
import { echo } from "./utils/echo";
import { useToast } from "./ToastContext";

export const STORAGE_KEY = process.env.REACT_APP_STORAGE_KEY;
export const MAX_AGE_DAYS = 3;

export default function Application() {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const currentUser = useSelector(state => state.authReducer.user);

  // useGetMe({
  //   onSuccess: response => {
  //     let user = get(response, "data.user", null);
  //     const userPermissions = [];

  //     user?.roles?.forEach(role => userPermissions.push(...role.permissions));

  //     user = {
  //       ...user,
  //       permissions: convertPermission(userPermissions)
  //     };

  //     dispatch(createUserSuccess(user));
  //   }
  // });

  useEffect(() => {
    if (!currentUser?.id) return;

    const getStoredPayloads = () => {
      try {
        let user = currentUser;
        const userPermissions = [];

        user?.roles?.forEach(role => userPermissions.push(...role.permissions));

        user = {
          ...user,
          permissions: convertPermission(userPermissions)
        };

        dispatch(createUserSuccess(user));

        const data = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
        const now = Date.now();

        return data.filter(item => now - item.timestamp < MAX_AGE_DAYS * 24 * 60 * 60 * 1000);
      } catch {
        return [];
      }
    };

    const savePayload = payload => {
      const stored = getStoredPayloads();
      stored.push({
        ...payload,
        is_read: false,
        timestamp: Date.now()
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
      window.dispatchEvent(new Event("new-notification"));
    };

    const channelName = `order_status.${currentUser.id}`;
    const channel = echo.channel(channelName);

    channel.listenToAll((eventName, payload) => {
      savePayload(payload);
      showToast({
        severity: "success",
        summary: t("notifications.statusChanged"),
        detail: t("notifications.statusChangedDetail")
      });
    });

    // eslint-disable-next-line consistent-return
    return () => {
      channel.stopListeningToAll();
      echo.leaveChannel(channelName);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<WelcomePage />} />
        <Route path="/users/roles" element={<UserRoles />} />
        <Route path="/users/roles/create" element={<CreateUpdateRole />} />
        <Route path="/users/roles/edit/:id" element={<CreateUpdateRole />} />
        <Route path="/users/users" element={<Users />} />
        <Route path="/users/customers" element={<Customers />} />

        <Route path="/categories" element={<Categories />} />
        <Route path="/categories/create" element={<CreateUpdateCategory />} />
        <Route path="/categories/edit/:id" element={<CreateUpdateCategory />} />

        <Route path="/dealers" element={<Branches />} />
        <Route path="/dealers/create" element={<CreateUpdateBranch />} />
        <Route path="/dealers/edit/:id" element={<CreateUpdateBranch />} />

        <Route path="/stock/products" element={<Products />} />
        <Route path="/stock/brands" element={<Brands />} />
        <Route path="/stock/warehouses" element={<Warehouses />} />
        <Route path="/stock/orders" element={<StockOrders />} />
        <Route path="/stock/orders/create" element={<CreateUpdateStockOrder />} />
        <Route path="/stock/orders/edit/:id" element={<CreateUpdateStockOrder />} />

        <Route path="/order/return" element={<StockOrdersReturn />} />
        <Route path="/order/return/create" element={<CreateUpdateOrdersReturn />} />
        <Route path="/order/return/edit/:id" element={<CreateUpdateOrdersReturn />} />

        <Route path="/setting/measures" element={<Measures />} />
        <Route path="/setting/terminals" element={<Terminals />} />
        <Route path="/setting/cashboxes" element={<Cashboxes />} />
        <Route path="/setting/countries" element={<Countries />} />
        <Route path="/setting/regions" element={<Regions />} />
        <Route path="/setting/districts" element={<Districts />} />
        <Route path="/setting/z-index" element={<ZIndex />} />

        <Route path="/payment/currencies" element={<Currencies />} />
        <Route path="/payment/price_types" element={<PriceTypes />} />
        <Route path="/payment/payments" element={<Payments />} />
        <Route path="/payment/price" element={<PriceSettings />} />

        <Route path="/admin/banners" element={<Banners />} />
        <Route path="/admin/reviews" element={<Reviews />} />
        <Route path="/admin/offices" element={<Offices />} />
        <Route path="/admin/files" element={<Files />} />
        <Route path="/admin/posts" element={<Posts />} />
        <Route path="/admin/settings" element={<Settings />} />
        <Route path="/admin/subdivisions" element={<Subdivisions />} />
        <Route path="/admin/positions" element={<Positions />} />
        <Route path="/admin/employees" element={<Employees />} />
        <Route path="/admin/colors" element={<Colors />} />
        <Route path="/admin/stickers" element={<Stickers />} />
        <Route path="/admin/groups" element={<Groups />} />
        <Route path="/admin/abouts" element={<Abouts />} />
        <Route path="/admin/product_sizes" element={<ProductSizes />} />
        <Route path="/admin/variations" element={<Variations />} />
        <Route path="/admin/variations/create" element={<CreateUpdateVariation />} />
        <Route path="/admin/variations/edit/:id" element={<CreateUpdateVariation />} />

        <Route path="/reports/stock" element={<StockReports />} />
        <Route path="/reports/dealer-balance" element={<DealerBalance />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  );
}
